/* eslint-disable @typescript-eslint/no-explicit-any */
import { CalendarFilter } from "@/Components/CalendarFilter/CalendarFilter";
import type { CalendarFilterValue } from "@/Components/CalendarFilter/types";
import { useCalendarStore } from "../store/useCalendarStore";

export function Filter() {
  // Selected slice by slice: subscribing to the whole store re-renders the
  // drawer on unrelated calendar state (drag, fetch flags), which rebuilds the
  // select collections and makes an open dropdown flicker.
  const filterItems = useCalendarStore((state) => state.filterItems);
  // Not `filterItems.FK_locationId`: that only updates once a calendar refresh
  // lands, and `refreshCalendar` drops the call outright while another fetch is
  // in flight — leaving the drawer pinned to the previous location.
  const selectedLocationId = useCalendarStore(
    (state) => state.selectedLocationId,
  );
  const refreshWithNewFilter = useCalendarStore(
    (state) => state.refreshWithNewFilter,
  );
  const refreshCalendar = useCalendarStore((state) => state.refreshCalendar);
  const locationActions = useCalendarStore((state) => state.locationActions);
  const showOnlyFixedAppointments = useCalendarStore(
    (state) => state.showOnlyFixedAppointments,
  );
  const setShowOnlyFixedAppointments = useCalendarStore(
    (state) => state.setShowOnlyFixedAppointments,
  );

  // `showOnlyFixedAppointments` lives in the store rather than the saved
  // filter, so it is applied here instead of being sent with the rest of it.
  async function saveFilter(value: CalendarFilterValue) {
    setShowOnlyFixedAppointments(!!value.showOnlyFixedAppointments);
    await refreshWithNewFilter(toStoredFilter(value));
  }

  function changeLocation(locationId: number) {
    locationActions.setSelectedLocationId(locationId);
    void refreshCalendar();
  }

  return (
    <CalendarFilter
      value={toFilterValue(filterItems, showOnlyFixedAppointments)}
      onSave={saveFilter}
      locationId={selectedLocationId ?? null}
      onLocationChange={changeLocation}
      showCalendarMode
      showCalendarSettings
      showFixedAppointmentsToggle
      showExportPdf
    />
  );
}

function toFilterValue(
  filterItems: any,
  showOnlyFixedAppointments: boolean,
): CalendarFilterValue {
  return {
    locationId: filterItems.FK_locationId ?? null,
    users: filterItems.users ?? [],
    services: filterItems.services ?? [],
    resources: filterItems.resources ?? [],
    days: filterItems.days ?? [],
    calendarMode: filterItems.calendarMode,
    timeStep: filterItems.time_step,
    showOnlyUsersWithShifts: !!filterItems.showOnlyUsersWithShifts,
    showStaffNames: !!filterItems.showStaffNames,
    showOnlyFixedAppointments,
  };
}

function toStoredFilter(value: CalendarFilterValue) {
  return {
    FK_locationId: value.locationId,
    users: value.users,
    services: value.services,
    resources: value.resources,
    days: value.days,
    calendarMode: value.calendarMode,
    time_step: value.timeStep,
    showOnlyUsersWithShifts: value.showOnlyUsersWithShifts,
    showStaffNames: value.showStaffNames,
  };
}
